import React, { useEffect, useMemo, useRef, useState } from 'react';
import { supabaseClient } from '../../services/supabase';
import { ChartRendererSupabase } from './ChartRendererSupabase';
import { getColorsForCategories } from './colors';

type HeatingFuelRow = {
  house_heating_fuel: string | null;
  year: number | null;
  // Current cleaned CSV uses `percent_hh`.
  percent_hh?: number | string | null;
  // Older API-backed charts used `percent_of_households` (fallback only).
  percent_of_households?: number | string | null;
};

type ChartBundle = {
  chartData: any;
  chartOptions: any;
  smallChartData?: any;
  smallChartOptions?: any;
  compareMainData?: any;
  compareMainOptions?: any;
  compareOtherData?: any;
  compareOtherOptions?: any;
  fossilTrendData?: any;
  fossilTrendOptions?: any;
  fossilCompareData?: any;
  fossilCompareOptions?: any;
  indicator1: string;
  indicator2: string;
  indicator3?: string;
  indicatorLabel1: string;
  indicatorLabel2: string;
  indicatorLabel3?: string;
  smartNarrative: string;
};

const ORDERED_FUELS = [
  'Utility Gas',
  'Electricity',
  'Bottled, tank, or LP gas',
  'Fuel oil, kerosene, etc.',
  'Wood',
  'Solar energy',
  'Coal or coke',
  'Other fuel',
  'No fuel used',
];

const FUEL_NAME_LOOKUP = new Map(
  ORDERED_FUELS.map((f) => [f.toLowerCase(), f])
);

function normalizeFuelName(raw: string): string {
  return FUEL_NAME_LOOKUP.get(raw.toLowerCase()) ?? raw;
}

export interface HeatingFuelMetrics {
  gasPercent: number;
  elecPercent: number;
  fossilPercent: number;
  latestYear: number;
  baselineYear: number;
  elecShareChangePp: number | null;
  topFuelType: string;
  topFuelPercent: number;
  secondFuelType: string;
  secondFuelPercent: number;
  smartNarrative: string;
}

interface HouseHeatingFuelProps {
  locationName: string;
  onMetricsLoaded?: (metrics: HeatingFuelMetrics) => void;
  chartMode?: 'main' | 'other' | 'fossil' | 'compare';
}

export default function HouseHeatingFuelSupabase({ locationName, onMetricsLoaded, chartMode = 'main' }: HouseHeatingFuelProps) {
  const [bundle, setBundle] = useState<ChartBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [compareCategory, setCompareCategory] = useState<'major' | 'other' | 'fossil'>('major');
  const onMetricsLoadedRef = useRef(onMetricsLoaded);
  onMetricsLoadedRef.current = onMetricsLoaded;

  const title = useMemo(() => `Primary Heating Fuel Use - ${locationName}`, [locationName]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        // PostgREST will error if you select a column that doesn't exist, so we try
        // the current schema first and fall back to the legacy column name.
        let data: any[] | null = null;
        let sbError: any = null;

        {
          const res = await supabaseClient
            .from('house_heating_fuel')
            .select('house_heating_fuel, year, percent_hh')
            .ilike('location_name', locationName)
            .order('year', { ascending: true });
          data = res.data as any[] | null;
          sbError = res.error;
        }

        if (sbError && String(sbError.message || '').includes('percent_hh')) {
          const resLegacy = await supabaseClient
            .from('house_heating_fuel')
            .select('house_heating_fuel, year, percent_of_households')
            .ilike('location_name', locationName)
            .order('year', { ascending: true });
          data = resLegacy.data as any[] | null;
          sbError = resLegacy.error;
        }

        if (sbError) throw sbError;

        let caData: any[] | null = null;
        let caError: any = null;
        {
          const res = await supabaseClient
            .from('house_heating_fuel')
            .select('house_heating_fuel, year, percent_hh')
            .or('location_name.ilike.california,location_name.ilike.state of california')
            .order('year', { ascending: true });
          caData = res.data as any[] | null;
          caError = res.error;
        }
        if (caError && String(caError.message || '').includes('percent_hh')) {
          const resLegacy = await supabaseClient
            .from('house_heating_fuel')
            .select('house_heating_fuel, year, percent_of_households')
            .or('location_name.ilike.california,location_name.ilike.state of california')
            .order('year', { ascending: true });
          caData = resLegacy.data as any[] | null;
          caError = resLegacy.error;
        }
        if (caError) throw caError;

        const rows = (data || []) as HeatingFuelRow[];
        const cleaned = rows
          .filter((r) => {
            const pct = r.percent_hh ?? r.percent_of_households;
            return r.year != null && r.house_heating_fuel && pct != null;
          })
          .map((r) => ({
            year: Number(r.year),
            fuel: normalizeFuelName(String(r.house_heating_fuel)),
            pct: Number(r.percent_hh ?? r.percent_of_households),
          }))
          .filter((r) => Number.isFinite(r.year) && Number.isFinite(r.pct));

        const years = Array.from(new Set(cleaned.map((r) => r.year))).sort((a, b) => a - b);
        const fuelMap = new Map<string, Map<number, number>>();
        cleaned.forEach((r) => {
          if (!fuelMap.has(r.fuel)) fuelMap.set(r.fuel, new Map());
          fuelMap.get(r.fuel)!.set(r.year, r.pct);
        });

        const cleanedCA = ((caData || []) as HeatingFuelRow[])
          .filter((r) => {
            const pct = r.percent_hh ?? r.percent_of_households;
            return r.year != null && r.house_heating_fuel && pct != null;
          })
          .map((r) => ({
            year: Number(r.year),
            fuel: normalizeFuelName(String(r.house_heating_fuel)),
            pct: Number(r.percent_hh ?? r.percent_of_households),
          }))
          .filter((r) => Number.isFinite(r.year) && Number.isFinite(r.pct));
        const caFuelMap = new Map<string, Map<number, number>>();
        cleanedCA.forEach((r) => {
          if (!caFuelMap.has(r.fuel)) caFuelMap.set(r.fuel, new Map());
          caFuelMap.get(r.fuel)!.set(r.year, r.pct);
        });

        const fuels = ORDERED_FUELS.filter((f) => fuelMap.has(f));
        // If dataset includes fuels outside our list, append them at end.
        for (const f of Array.from(fuelMap.keys())) {
          if (!fuels.includes(f)) fuels.push(f);
        }

        const palette = getColorsForCategories(fuels);
        const datasets = fuels.map((fuel, idx) => ({
          label: fuel,
          data: years.map((y) => fuelMap.get(fuel)?.get(y) ?? null),
          borderColor: palette[idx],
          backgroundColor: palette[idx],
          tension: 0.3,
          borderWidth: 2,
          fill: false,
        }));

        const latestYear = years[years.length - 1];
        const baselineYear = years[0];
        const latestRows = cleaned.filter((r) => r.year === latestYear);
        const baselineRows = cleaned.filter((r) => r.year === baselineYear);
        const gas = latestRows.find((r) => r.fuel === 'Utility Gas')?.pct ?? 0;
        const elec = latestRows.find((r) => r.fuel === 'Electricity')?.pct ?? 0;
        const baselineElec = baselineRows.find((r) => r.fuel === 'Electricity')?.pct;
        const elecShareChangePp =
          baselineElec != null && Number.isFinite(baselineElec) ? elec - baselineElec : null;
        const rankedLatest = [...latestRows]
          .filter((r) => Number.isFinite(r.pct))
          .sort((a, b) => b.pct - a.pct);
        const topFuelType = rankedLatest[0]?.fuel ?? '—';
        const topFuelPercent = rankedLatest[0]?.pct ?? 0;
        const secondFuelType = rankedLatest[1]?.fuel ?? '—';
        const secondFuelPercent = rankedLatest[1]?.pct ?? 0;
        const fossilFuels = new Set([
          'Utility Gas',
          'Bottled, tank, or LP gas',
          'Fuel oil, kerosene, etc.',
          'Coal or coke',
          'Other fuel',
        ]);
        const fossilByYearLocal = years.map((y) =>
          fuels.reduce((sum, f) => sum + (fossilFuels.has(f) ? fuelMap.get(f)?.get(y) ?? 0 : 0), 0)
        );
        const fossilByYearCA = years.map((y) =>
          fuels.reduce((sum, f) => sum + (fossilFuels.has(f) ? caFuelMap.get(f)?.get(y) ?? 0 : 0), 0)
        );
        const latestFossil = fossilByYearLocal[years.length - 1] ?? 0;

        const smartNarrative = `In ${locationName}, ${gas.toFixed(1)}% of households use natural gas as their primary heating fuel, while ${elec.toFixed(1)}% use electricity (latest year: ${latestYear}). This distribution provides a simple indicator of building electrification progress.`;

        // Build zoomed-in chart for smaller fuels (exclude utility gas and electricity)
        const majorFuels = new Set(['Utility Gas', 'Electricity']);
        const smallFuels = fuels.filter((f) => !majorFuels.has(f));
        const smallDatasets = smallFuels.map((fuel, idx) => {
          const paletteIdx = fuels.indexOf(fuel);
          const color = palette[paletteIdx >= 0 ? paletteIdx : idx];
          return {
            label: fuel,
            data: years.map((y) => fuelMap.get(fuel)?.get(y) ?? null),
            borderColor: color,
            backgroundColor: color,
            tension: 0.3,
            borderWidth: 2,
            fill: false,
          };
        });

        const hasSmallData = smallDatasets.some((ds) =>
          (ds.data || []).some((v: number | null) => v != null && !Number.isNaN(v) && v > 0)
        );

        const compareMainData = {
          labels: years,
          datasets: [
            {
              label: `${locationName} - Utility Gas`,
              data: years.map((y) => fuelMap.get('Utility Gas')?.get(y) ?? null),
              borderColor: '#1d4ed8',
              backgroundColor: 'transparent',
              borderWidth: 2.5,
              tension: 0.25,
              pointRadius: 3,
            },
            {
              label: 'California - Utility Gas',
              data: years.map((y) => caFuelMap.get('Utility Gas')?.get(y) ?? null),
              borderColor: '#60a5fa',
              backgroundColor: 'transparent',
              borderWidth: 2,
              borderDash: [6, 4],
              tension: 0.25,
              pointRadius: 2,
            },
            {
              label: `${locationName} - Electricity`,
              data: years.map((y) => fuelMap.get('Electricity')?.get(y) ?? null),
              borderColor: '#b45309',
              backgroundColor: 'transparent',
              borderWidth: 2.5,
              tension: 0.25,
              pointRadius: 3,
            },
            {
              label: 'California - Electricity',
              data: years.map((y) => caFuelMap.get('Electricity')?.get(y) ?? null),
              borderColor: '#fbbf24',
              backgroundColor: 'transparent',
              borderWidth: 2,
              borderDash: [6, 4],
              tension: 0.25,
              pointRadius: 2,
            },
          ],
        };

        const compareMainOptions = {
          type: 'line',
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { position: 'bottom' },
            title: { display: true, text: `${locationName} vs California (Utility Gas & Electricity)` },
          },
          scales: {
            y: { beginAtZero: true, max: 100, title: { display: true, text: '% of Households' } },
            x: { title: { display: true, text: 'Year' } },
          },
        };

        const localOtherTotal = years.map((y) =>
          smallFuels.reduce((sum, f) => sum + (fuelMap.get(f)?.get(y) ?? 0), 0)
        );
        const caOtherTotal = years.map((y) =>
          smallFuels.reduce((sum, f) => sum + (caFuelMap.get(f)?.get(y) ?? 0), 0)
        );
        const compareOtherData = {
          labels: years,
          datasets: [
            {
              label: `${locationName} - Other fuels total`,
              data: localOtherTotal,
              borderColor: '#0f766e',
              backgroundColor: 'rgba(15, 118, 110, 0.08)',
              borderWidth: 2.5,
              tension: 0.25,
              pointRadius: 3,
              fill: true,
            },
            {
              label: 'California - Other fuels total',
              data: caOtherTotal,
              borderColor: '#34d399',
              backgroundColor: 'transparent',
              borderWidth: 2,
              borderDash: [6, 4],
              tension: 0.25,
              pointRadius: 2,
              fill: false,
            },
          ],
        };
        const compareOtherOptions = {
          type: 'line',
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { position: 'bottom' },
            title: { display: true, text: `${locationName} vs California (Other fuels total)` },
          },
          scales: {
            y: { beginAtZero: true, title: { display: true, text: '% of Households' } },
            x: { title: { display: true, text: 'Year' } },
          },
        };

        const fossilTrendData = {
          labels: years,
          datasets: [
            {
              label: `${locationName} - Fossil heating share`,
              data: fossilByYearLocal,
              borderColor: '#7c2d12',
              backgroundColor: 'rgba(124, 45, 18, 0.08)',
              borderWidth: 2.5,
              tension: 0.25,
              pointRadius: 3,
              fill: true,
            },
          ],
        };
        const fossilTrendOptions = {
          type: 'line',
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { position: 'bottom' },
            title: { display: true, text: 'Fossil Heating Share Over Time (Local)' },
          },
          scales: {
            y: { beginAtZero: true, max: 100, title: { display: true, text: '% of Households' } },
            x: { title: { display: true, text: 'Year' } },
          },
        };
        const fossilCompareData = {
          labels: years,
          datasets: [
            {
              label: `${locationName} - Fossil heating share`,
              data: fossilByYearLocal,
              borderColor: '#7c2d12',
              backgroundColor: 'rgba(124, 45, 18, 0.08)',
              borderWidth: 2.5,
              tension: 0.25,
              pointRadius: 3,
              fill: false,
            },
            {
              label: 'California - Fossil heating share',
              data: fossilByYearCA,
              borderColor: '#f97316',
              backgroundColor: 'transparent',
              borderWidth: 2,
              borderDash: [6, 4],
              tension: 0.25,
              pointRadius: 2,
              fill: false,
            },
          ],
        };
        const fossilCompareOptions = {
          type: 'line',
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { position: 'bottom' },
            title: { display: true, text: `${locationName} vs California - Fossil Heating Share` },
          },
          scales: {
            y: { beginAtZero: true, max: 100, title: { display: true, text: '% of Households' } },
            x: { title: { display: true, text: 'Year' } },
          },
        };

        const next: ChartBundle = {
          chartData: { labels: years, datasets },
          chartOptions: {
            type: 'line',
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { position: 'bottom' },
              title: { display: true, text: 'Primary Heating Fuel Use' },
              tooltip: {
                callbacks: {
                  label: (ctx: any) =>
                    `${ctx.dataset.label}: ${typeof ctx.parsed?.y === 'number' ? ctx.parsed.y.toFixed(1) : 'N/A'}%`,
                },
              },
            },
            scales: {
              y: { beginAtZero: true, title: { display: true, text: '% of Households' } },
              x: { title: { display: true, text: 'Year' } },
            },
          },
          smallChartData: hasSmallData ? { labels: years, datasets: smallDatasets } : undefined,
          smallChartOptions: hasSmallData
            ? {
                type: 'line',
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: { position: 'bottom' },
                  title: { display: true, text: 'Other Heating Fuels (Zoomed In)' },
                  tooltip: {
                    callbacks: {
                      label: (ctx: any) =>
                        `${ctx.dataset.label}: ${
                          typeof ctx.parsed?.y === 'number' ? ctx.parsed.y.toFixed(1) : 'N/A'
                        }%`,
                    },
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    max: 10,
                    title: { display: true, text: '% of Households' },
                  },
                  x: { title: { display: true, text: 'Year' } },
                },
              }
            : undefined,
          compareMainData,
          compareMainOptions,
          compareOtherData,
          compareOtherOptions,
          fossilTrendData,
          fossilTrendOptions,
          fossilCompareData,
          fossilCompareOptions,
          indicator1: `${gas.toFixed(1)}%`,
          indicator2: `${elec.toFixed(1)}%`,
          indicator3: `${latestFossil.toFixed(1)}%`,
          indicatorLabel1: 'Households using utility gas (latest year)',
          indicatorLabel2: 'Households using electricity (latest year)',
          indicatorLabel3: 'Fossil heating share (latest year)',
          smartNarrative,
        };

        if (!cancelled) {
          setBundle(next);
          onMetricsLoadedRef.current?.({
            gasPercent: gas,
            elecPercent: elec,
            fossilPercent: latestFossil,
            latestYear,
            baselineYear,
            elecShareChangePp,
            topFuelType,
            topFuelPercent,
            secondFuelType,
            secondFuelPercent,
            smartNarrative
          });
        }
      } catch (e: any) {
        if (!cancelled) {
          setBundle(null);
          setError(e?.message || 'Failed to load heating fuel data.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [locationName]);

  if (loading) {
    return (
      <div className="placeholder-chart">
        <p>Loading heating fuel data…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="placeholder-chart">
        <p>Couldn’t load heating fuel data.</p>
        <small style={{ opacity: 0.8 }}>{error}</small>
      </div>
    );
  }

  if (!bundle) {
    return (
      <div className="placeholder-chart">
        <p>No heating fuel data found for {locationName}.</p>
      </div>
    );
  }

  return (
    <div>
      {chartMode === 'compare' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setCompareCategory('major')}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: compareCategory === 'major' ? '1px solid #1d4ed8' : '1px solid #d1d5db',
                background: compareCategory === 'major' ? '#eff6ff' : '#ffffff',
                color: compareCategory === 'major' ? '#1d4ed8' : '#374151',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Utility Gas & Electricity
            </button>
            <button
              type="button"
              onClick={() => setCompareCategory('other')}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: compareCategory === 'other' ? '1px solid #0f766e' : '1px solid #d1d5db',
                background: compareCategory === 'other' ? '#ecfeff' : '#ffffff',
                color: compareCategory === 'other' ? '#0f766e' : '#374151',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Other fuels
            </button>
            <button
              type="button"
              onClick={() => setCompareCategory('fossil')}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: compareCategory === 'fossil' ? '1px solid #7c2d12' : '1px solid #d1d5db',
                background: compareCategory === 'fossil' ? '#fff7ed' : '#ffffff',
                color: compareCategory === 'fossil' ? '#7c2d12' : '#374151',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Percent fossil
            </button>
          </div>
          {compareCategory === 'major' && bundle.compareMainData && bundle.compareMainOptions && (
            <ChartRendererSupabase
              chartData={bundle.compareMainData}
              chartOptions={bundle.compareMainOptions}
              height="320px"
              title={`${title} – Main Fuels vs California`}
              location={locationName}
            />
          )}
          {compareCategory === 'other' && bundle.compareOtherData && bundle.compareOtherOptions && (
            <ChartRendererSupabase
              chartData={bundle.compareOtherData}
              chartOptions={bundle.compareOtherOptions}
              height="300px"
              title={`${title} – Other Fuels vs California`}
              location={locationName}
            />
          )}
          {compareCategory === 'fossil' && bundle.fossilCompareData && bundle.fossilCompareOptions && (
            <ChartRendererSupabase
              chartData={bundle.fossilCompareData}
              chartOptions={bundle.fossilCompareOptions}
              height="280px"
              title={`${title} – Fossil Share vs California`}
              location={locationName}
            />
          )}
        </div>
      ) : chartMode === 'other' ? (
        bundle.smallChartData && bundle.smallChartOptions ? (
          <ChartRendererSupabase
            chartData={bundle.smallChartData}
            chartOptions={bundle.smallChartOptions}
            height="360px"
            title={`${title} – Other Fuels`}
            location={locationName}
          />
        ) : (
          <div className="placeholder-chart">
            <p>No "other fuels" data available for {locationName}.</p>
          </div>
        )
      ) : chartMode === 'fossil' ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {bundle.fossilTrendData && bundle.fossilTrendOptions && (
            <ChartRendererSupabase
              chartData={bundle.fossilTrendData}
              chartOptions={bundle.fossilTrendOptions}
              height="360px"
              title={`${title} – Fossil Heating Share`}
              location={locationName}
            />
          )}
        </div>
      ) : (
        <ChartRendererSupabase
          chartData={bundle.chartData}
          chartOptions={bundle.chartOptions}
          height="420px"
          title={title}
          location={locationName}
        />
      )}
      <div style={{ marginTop: 12 }}>
        <small style={{ opacity: 0.85 }}>{bundle.smartNarrative}</small>
      </div>
    </div>
  );
}

